import {omit} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import type {GetMyCalendarSelectedUsersApiResponse} from '@dms/api/core';
import type {GetCurrentUserInfoApiResponse, UserResponseBody} from '@dms/api/shared';
import {getNaturalPersonFullName} from '@dms/shared';

import {EMPTY_PLACEHOLDER, type Nullish} from 'shared/web';

import {getUserColor} from './getUserColor';

type GetSelectedCalendarUsersParams = {
  usersData: UserResponseBody[] | Nullish;
  selectedUsers: GetMyCalendarSelectedUsersApiResponse | Nullish;
  currentUserData: GetCurrentUserInfoApiResponse | Nullish;
};

export function getSelectedCalendarUsers(params: GetSelectedCalendarUsersParams) {
  if (!params.currentUserData) {
    return [];
  }

  const currentUserFromSelectedUsers = params.selectedUsers?.find(
    (user) => user.userId === params.currentUserData!.id
  );

  const currentUser = {
    ...omit(['settings', 'firstName', 'lastName'], params.currentUserData),
    fullName:
      getNaturalPersonFullName({
        firstName: params.currentUserData.firstName,
        lastName: params.currentUserData.lastName,
      }) || EMPTY_PLACEHOLDER,
    isHidden: !!currentUserFromSelectedUsers?.isHidden,
    color: getUserColor(0),
  };

  if (!params.usersData?.length || !params.selectedUsers?.length) {
    return [currentUser];
  }

  const selectedCalendarUsers = params.selectedUsers
    .map((selectedUser) => {
      const user = params.usersData!.find(
        (u) => u.id === selectedUser.userId && u.id !== currentUser.id && !u.blocked
      );

      if (!user) {
        return null;
      }

      return {
        ...omit(['firstName', 'lastName', 'blocked'], user),
        fullName:
          getNaturalPersonFullName({
            firstName: user.firstName,
            lastName: user.lastName,
          }) || EMPTY_PLACEHOLDER,
        isHidden: selectedUser.isHidden,
      };
    })
    .filter(isNotNil)
    // Color is derived from the user's position in the final list (current user is index 0)
    // so header chips always match event colors, which use the same index.
    .map((user, index) => ({...user, color: getUserColor(index + 1)}));

  return [currentUser, ...selectedCalendarUsers];
}
